import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export interface SpecializationProposalOutcome {
  specializationId: string;
  proposalLabel: string;
  proposalCategory: string;
  outcome: 'accepted' | 'rejected';
  decidedAtMillis: number;
  acceptedSpecializationId: string | null;
}

interface GetOwnSpecializationProposalOutcomeInput {
  specializationId: string;
}

const isSafeDocumentId = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9_-]{1,256}$/.test(value.trim());

const isValidOutcome = (value: unknown): value is SpecializationProposalOutcome['outcome'] =>
  value === 'accepted' || value === 'rejected';

const parseOutcome = (value: unknown): SpecializationProposalOutcome => {
  if (!value || typeof value !== 'object') {
    throw new Error('Nieprawidłowa odpowiedź decyzji specjalizacji.');
  }

  const data = value as Record<string, unknown>;
  const specializationId = data.specializationId;
  const proposalLabel = data.proposalLabel;
  const proposalCategory = data.proposalCategory;
  const outcome = data.outcome;
  const decidedAtMillis = data.decidedAtMillis;
  const acceptedSpecializationId = data.acceptedSpecializationId;

  if (
    !isSafeDocumentId(specializationId) ||
    typeof proposalLabel !== 'string' ||
    !proposalLabel.trim() ||
    typeof proposalCategory !== 'string' ||
    !proposalCategory.trim() ||
    !isValidOutcome(outcome) ||
    typeof decidedAtMillis !== 'number' ||
    !Number.isSafeInteger(decidedAtMillis) ||
    decidedAtMillis <= 0 ||
    (acceptedSpecializationId !== null && !isSafeDocumentId(acceptedSpecializationId)) ||
    (outcome === 'accepted' && acceptedSpecializationId !== specializationId) ||
    (outcome === 'rejected' && acceptedSpecializationId !== null)
  ) {
    throw new Error('Nieprawidłowa odpowiedź decyzji specjalizacji.');
  }

  return {
    specializationId: specializationId.trim(),
    proposalLabel: proposalLabel.trim(),
    proposalCategory: proposalCategory.trim(),
    outcome,
    decidedAtMillis,
    acceptedSpecializationId:
      acceptedSpecializationId === null ? null : acceptedSpecializationId.trim(),
  };
};

export const getOwnSpecializationProposalOutcome = async (
  specializationId: string
): Promise<SpecializationProposalOutcome> => {
  const normalizedId = String(specializationId || '').trim();
  if (!isSafeDocumentId(normalizedId)) {
    throw new Error('Nieprawidłowy identyfikator propozycji specjalizacji.');
  }

  const callable = httpsCallable<GetOwnSpecializationProposalOutcomeInput, unknown>(
    functions,
    'getOwnSpecializationProposalOutcome'
  );
  const result = await callable({ specializationId: normalizedId });
  return parseOutcome(result.data);
};
